import React, { useCallback, useMemo, useState } from "react";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { FormHelperText } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import Input from "@components/Input/Input";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { resolveDocumentName } from "@features/port-security/components/utils";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import NotifyDropdown from "../components/NotifyDropdown";
import { getNotifyOptionRequiredMessage } from "../flow/utils";
import type { UploadDocumentDrawerInfo } from "../hoc/withDrawers";
import { useSendPortServiceDocument } from "../hooks/useSendPortServiceDocument";
import { NOTIFY_LIST } from "../preview/utils";

const LANG_BASE = "vessel-call.port-services";
const UPLOAD_KEY = "port-service-drawer-document";
const DOCUMENT_TYPE = "ADDITIONAL DOCUMENTS";

const uploadDocumentSchema = z.object({
  fileUrl: z.string().min(1, { message: "Please upload a file" }),
  notifyOption: z.array(z.string()).min(1, { message: getNotifyOptionRequiredMessage() }),
});

interface UploadDocumentDrawerProps {
  drawerInfo: UploadDocumentDrawerInfo | null;
  onClose: () => void;
  setShouldRefetch: (value: boolean) => void;
}
const asDateOrNull = (v: unknown) => (v == null || v === "" ? null : (v as Date));

const UploadDocumentDrawer: React.FC<UploadDocumentDrawerProps> = ({
  drawerInfo,
  onClose,
  setShouldRefetch,
}) => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [notifyOption, setNotifyOption] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<{
    fileUrl?: string;
    notifyOption?: string;
  }>({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState<string>("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const files = useUploadStore((s) => s.filesByKey[UPLOAD_KEY] ?? EMPTY_UPLOAD_ENTRIES);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const clearFilesForKey = useUploadStore((state) => state.clearFilesForKey);

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { additionalDocuments: uploadConditions } = FILE_UPLOAD_PROFILES;

  const allowedExtensions = useMemo(
    () =>
      (uploadConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [uploadConditions.format],
  );

  const setSingleFile = useCallback(
    (next: any) => setFilesForKey(UPLOAD_KEY, next, { maxFiles: 1 }),
    [setFilesForKey],
  );

  const displayTitle =
    documentTitle ||
    resolveDocumentName({
      name: files[0]?.name,
      url: uploadedUrl,
      type: DOCUMENT_TYPE,
      fallback: "Document",
    });

  const handleReset = () => {
    clearFilesForKey(UPLOAD_KEY);
    setUploadedUrl("");
    setDocumentTitle("");
    setExpirationDate(null);
    setError("");
  };
  const { onSubmit } = useSendPortServiceDocument({
    portServiceId: drawerInfo?.id || "",
    name: displayTitle,
    url: uploadedUrl,
    originalFileName: files[0]?.name,
    expirationDate: expirationDate ?? null,
    notifyList: notifyOption ?? [],
    setShouldRefetch: setShouldRefetch,
    onClose: onClose,
    handleReset: handleReset,
  });

  const handleUpload = async (incomingFiles: File[]) => {
    setError("");
    setErrors((prev) => ({ ...prev, fileUrl: undefined }));
    if (!incomingFiles?.length) return;

    const file = incomingFiles[incomingFiles.length - 1];
    setIsUploading(true);
    try {
      const payloadForSasToken = {
        prefix: SasTokenPrefix.PORT_SERVICES,
        contentType: file.type || null,
        privilege: SasTokenPrivilege.WRITE,
        blobUrl: null,
      };
      const { blobUrl, sasToken } = await getSasToken(payloadForSasToken);
      const result = await runTransaction(
        {
          module: "vessel_call",
          screen: "UploadPortServiceDocument",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        () =>
          uploadFileToAzureBlob({
            blobUrl,
            sasToken,
            file,
            certificateType: DOCUMENT_TYPE,
          }),
      );
      const documentUrl = result?.[0]?.documentUrl;
      if (!documentUrl) throw new Error("Document URL missing from upload response.");

      const resolvedName = resolveDocumentName({
        name: file.name,
        url: documentUrl,
        type: DOCUMENT_TYPE,
        fallback: "Document",
      });

      setSingleFile([{ name: resolvedName, url: documentUrl }]);
      setDocumentTitle(resolvedName);
      setUploadedUrl(documentUrl);
    } catch (uploadError) {
      notify({
        title: t("vessel-management.notifications.additional-documents-upload-error-title"),
        description: t(
          "vessel-management.notifications.additional-documents-upload-error-description",
        ),
        type: "error",
      });
      console.error("Error on upload documents:", uploadError);
      clearFilesForKey(UPLOAD_KEY);
      setUploadedUrl("");
    } finally {
      setIsUploading(false);
    }
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");
    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    );
  };

  const handleRemoveFile = () => {
    setUploadedUrl("");
    setDocumentTitle("");
    setExpirationDate(null);
    clearFilesForKey(UPLOAD_KEY);
  };

  const handleSubmit = async () => {
    const result = uploadDocumentSchema.safeParse({
      notifyOption: notifyOption,
      fileUrl: uploadedUrl,
    });

    if (!result.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of result.error.issues) {
        const pathKey = issue.path[0] as keyof typeof fieldErrors;
        if (!pathKey) continue;
        fieldErrors[pathKey] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    // clear previous errors and call original submit
    setErrors({});
    await onSubmit();
  };

  if (!drawerInfo) return null;

  return (
    <div data-testid="upload-port-service-file">
      <SideDrawer
        isOpen={!!drawerInfo?.isOpen}
        title={t(`${LANG_BASE}.upload-document-title`)}
        cancelLabel={t("common.cancel")}
        onClose={() => {
          handleReset();
          onClose();
        }}
        submitLabel={t("common.upload")}
        onSubmit={handleSubmit}
      >
        <div className="px-6">
          <div className="min-w-sm max-w-sm">
            <div className="flex flex-col gap-2 mb-4 relative rounded-sm border border-[#E6EDF2] p-4">
              {isUploading && (
                <div className="absolute inset-0 flex justify-center pointer-events-none top-30 opacity-100">
                  <RotateRightIcon
                    fontSize="large"
                    sx={{
                      "animation": "mui-spin 0.9s linear infinite",
                      "@keyframes mui-spin": { to: { transform: "rotate(360deg)" } },
                    }}
                  />
                </div>
              )}
              <div className={`${isUploading ? "opacity-50" : ""}`}>
                <FileUpload
                  testId="portService-drawer-attachment"
                  onUpload={handleUpload}
                  onReject={handleReject}
                  name={UPLOAD_KEY}
                  conditions={uploadConditions}
                  files={files}
                  setFiles={(next) => {
                    setSingleFile(next);
                    setErrors((prev) => ({ ...prev, fileUrl: undefined }));
                  }}
                  maxFiles={1}
                  allowedExtensions={allowedExtensions}
                  disabled={isUploading}
                  uploadDescription={t("upload.additional-documents-upload", {
                    extensions: uploadConditions.format.join(", "),
                    size: uploadConditions.maxSizeLabel,
                  })}
                />
                {error && (
                  <FormHelperText
                    sx={{
                      ml: 0,
                      fontWeight: 500,
                      fontSize: 12,
                      marginY: "4px",
                      height: "20px",
                      color: "error.main",
                    }}
                  >
                    {t("form-validation.file-type", { types: allowedExtensions.join(", ") })}
                  </FormHelperText>
                )}
              </div>
              {errors.fileUrl && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.fileUrl}</FormHelperText>
              )}
            </div>

            {files.length ? (
              <div className="flex flex-col gap-4">
                <SingleFile
                  testId="portService-drawer-attachment-file"
                  file={files[0]}
                  currentFileIndex={0}
                  fieldName={UPLOAD_KEY}
                  setFiles={() => handleRemoveFile()}
                />
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <CustomFormLabel>{t(`${LANG_BASE}.fields.document-title`)}</CustomFormLabel>
                    <Input
                      testId="upload-port-service-file-name"
                      fullWidth
                      value={documentTitle}
                      onChange={(e) => setDocumentTitle(e.target.value)}
                      variant="outlined"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <CustomFormLabel>{t(`${LANG_BASE}.fields.expiration-date`)}</CustomFormLabel>
                    <DatePicker
                      hasError={false}
                      value={asDateOrNull(expirationDate)}
                      onChange={(d) => setExpirationDate(d)}
                      testId={`expiration-date-input`}
                      timePicker={false}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-2 mt-6">
              <CustomFormLabel data-testid="change-status-notify-label">
                {t(`${LANG_BASE}.notify`)}
              </CustomFormLabel>
              <NotifyDropdown
                options={NOTIFY_LIST}
                option={notifyOption}
                setOption={(opt) => {
                  setNotifyOption(opt);
                  setErrors((prev) => ({ ...prev, notifyOption: undefined }));
                }}
              />
              {errors.notifyOption && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.notifyOption}
                </FormHelperText>
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default UploadDocumentDrawer;
